import type { MembershipType } from '@kalos/kalos-rpc';
import { format, startOfMonth, startOfYear, subDays } from 'date-fns';
import { z } from 'zod';

export const SALE_STATUSES = ['sold', 'active', 'pending', 'cancelled', 'all'] as const;
export const SECTORS = ['all', 'residential', 'commercial'] as const;

export type SaleStatus = (typeof SALE_STATUSES)[number];
export type SalesSector = (typeof SECTORS)[number];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

// Both dates fall back to '' so beforeLoad can redirect to the default range instead of
// silently querying one the user never picked.
export const membershipSalesSearchSchema = z.object({
  startDate: z.string().regex(DAY).catch(''),
  endDate: z.string().regex(DAY).catch(''),
  soldBy: z.number().int().catch(0),
  program: z.string().catch(''),
  tier: z.string().catch(''),
  sector: z.enum(SECTORS).catch('all'),
  status: z.enum(SALE_STATUSES).catch('sold'),
});

const asDay = (date: Date) => format(date, 'yyyy-MM-dd');

/**
 * Ranges offered by the date picker and the quick chips. End is always today;
 * recomputed per render. `short` is the chip label, `label` the dropdown one.
 */
export function dateRangePresets() {
  const today = new Date();
  return [
    { label: 'Year to date', short: 'YTD', value: { from: startOfYear(today), to: today } },
    { label: 'Month to date', short: 'MTD', value: { from: startOfMonth(today), to: today } },
    { label: 'Last 30 days', short: '30d', value: { from: subDays(today, 29), to: today } },
    { label: 'Last 90 days', short: '90d', value: { from: subDays(today, 89), to: today } },
  ];
}

/** Year to date — what the route falls back to and what Clear filters restores. */
export function defaultDateRange() {
  const [{ value }] = dateRangePresets();
  return { startDate: asDay(value.from), endDate: asDay(value.to) };
}

/** The core rejects a bad range or filter value with INVALID_ARGUMENT — that text is actionable. */
export function salesErrorText(error: Error | null) {
  const message = error?.message?.trim() ?? '';
  return /invalid|required|must/i.test(message)
    ? `${message} Check your filters.`
    : 'Could not load membership sales. Please try again.';
}

/** Short codes for the seller table's plan columns: program prefix + tier digit. */
export const PLAN_PROGRAM_CODES: Record<string, { code: string; label: string }> = {
  comfort: { code: 'KCF', label: 'Kalos Comfort Plan' },
  complete: { code: 'KCP', label: 'Kalos Complete Plan' },
};

export const PLAN_TIER_CODES: Record<string, { code: string; label: string }> = {
  premium: { code: '1', label: 'Premium' },
  standard: { code: '2', label: 'Standard' },
  snowbird: { code: '3', label: 'Snowbird' },
};

export const PLAN_TERMS = [...Object.values(PLAN_PROGRAM_CODES), ...Object.values(PLAN_TIER_CODES)]
  .map(({ code, label }) => `${code} = ${label}`)
  .join(' · ');

export interface PlanSalesColumn {
  program: string;
  tier: string;
  code: string;
}

/** `KCF1` when both parts have a short code, otherwise the pair's own words. */
export function planCode(program: string, tier: string) {
  const programCode = PLAN_PROGRAM_CODES[program]?.code;
  const tierCode = PLAN_TIER_CODES[tier]?.code;
  return programCode && tierCode ? `${programCode}${tierCode}` : `${program} ${tier}`;
}

/** One column per program + tier that has an active catalog plan, ordered by code. */
export function planSalesColumns(plans: MembershipType[]): PlanSalesColumn[] {
  const columns = new Map<string, PlanSalesColumn>();
  for (const plan of plans) {
    const program = plan.program.trim().toLowerCase();
    const tier = plan.tier.trim().toLowerCase();
    if (!plan.isActive || !program || !tier) {
      continue;
    }
    columns.set(`${program}/${tier}`, { program, tier, code: planCode(program, tier) });
  }
  return [...columns.values()].sort((a, b) => a.code.localeCompare(b.code));
}
